import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import STOCK_URL from "../../utils/STOCK_URL.js";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-container": {
    padding: "2rem",
  },
  "& .MuiDialog-paper": {
    backgroundColor: "#121212",
    opacity: 0.85,
    color: "#ffffff",
    width: 1100,
    height: 500,
    maxWidth: "none",
    maxHeight: "none",
    border: "1px solid #464646",
  },
}));
const DialogHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#000000",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.5rem 1.5rem",
}));
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
  "& .MuiInputLabel-root": {
    color: "#ffffff !important",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#ffffff !important",
  },
  "& .MuiOutlinedInput-root": {
    color: "#ffffff !important",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#464646 !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ffffff !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1976d2 !important",
    },
  },
  "& .MuiSvgIcon-root": {
    color: "#ffffff !important",
  },
}));
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: "#121212 !important",
  color: "#ffffff !important",
  "&:hover": {
    backgroundColor: "#333333 !important",
  },
  "&.Mui-selected": {
    backgroundColor: "#1976d2 !important",
  },
}));

function DayStockGraph({ open, currentMchId, setDayStockGraphOpen }) {
  const { group_code, project_code } = useParams();
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartData, setChartData] = useState([]);
  const [yAxisDomain, setYAxisDomain] = useState([0, "auto"]);
  useEffect(() => {
    async function fetchAvailableYears() {
      try {
        const result = await axios.get(
          STOCK_URL +
            `/api/stock/yearInfo?group_code=${group_code}&project_code=${project_code}&currentMchId=${currentMchId}`
        );
        setAvailableYears(result.data);
      } catch (e) {
        console.log(e);
      }
    }
    if (open) {
      fetchAvailableYears();
    }
  }, [open, group_code, project_code]);
  useEffect(() => {
    async function fetchDailyData() {
      try {
        const result = await axios.get(
          STOCK_URL +
            `/api/stock/dayGraph?group_code=${group_code}&project_code=${project_code}&currentMchId=${currentMchId}&target_year=${selectedYear}`
        );
        const formattedData = result.data.map((item) => {
          const date = new Date(item.snapshot_date);
          const formattedDate = `${date.getMonth() + 1}/${date.getDate()}`;
          return {
            date: formattedDate,
            fullDate: item.snapshot_date,
            입고누적: item.cumulative_stock_in || 0,
            출고누적: item.cumulative_stock_out || 0,
            재고량: item.daily_stock_quantity || 0,
          };
        });
        const allValues = formattedData.flatMap((item) => [
          item.입고누적,
          item.출고누적,
          item.재고량,
        ]);
        const maxValue = Math.max(...allValues);
        const yMax = Math.ceil(maxValue * 1.2);

        setChartData(formattedData);
        setYAxisDomain([0, yMax]);
      } catch (e) {
        console.log(e);
      }
    }
    if (open && selectedYear) {
      fetchDailyData();
    }
  }, [open, selectedYear, group_code, project_code, currentMchId]);
  if (!open) return null;
  const handleClose = () => setDayStockGraphOpen(false);
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${year}년 ${month}월 ${day}일`;
      };
      return (
        <div
          style={{
            backgroundColor: "#333333",
            border: "1px solid #464646",
            borderRadius: "4px",
            color: "#ffffff",
            padding: "10px",
          }}
        >
          <p style={{ margin: 0, marginBottom: "5px" }}>{`${formatDate(
            data.fullDate
          )}`}</p>
          <p
            style={{ margin: 0, color: "#22c55e" }}
          >{`입고누적: ${data.입고누적.toLocaleString()}`}</p>
          <p
            style={{ margin: 0, color: "#3b82f6" }}
          >{`출고누적: ${data.출고누적.toLocaleString()}`}</p>
          <p
            style={{ margin: 0, color: "#ff8787" }}
          >{`재고량: ${data.재고량.toLocaleString()}`}</p>
        </div>
      );
    }
    return null;
  };
  const formatXAxisTick = (tickItem, index) => {
    const dataPoint = chartData[index];
    if (dataPoint) {
      const date = new Date(dataPoint.fullDate);
      if (date.getDate() === 1) {
        return `${date.getMonth() + 1}/1`;
      }
    }
    return "";
  };
  const customLegendProps = {
    wrapperStyle: {
      paddingBottom: "20px",
    },
    iconType: "line",
  };
  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <DialogHeader>
        <Typography
          sx={{
            margin: 0,
            fontSize: "1.5em",
            fontWeight: 700,
            color: "#ffffff",
          }}
        >
          재고 추이
        </Typography>
        <StyledFormControl size="small">
          <InputLabel>연도</InputLabel>
          <Select
            value={selectedYear}
            onChange={handleYearChange}
            label="연도"
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#121212",
                  border: "1px solid #464646",
                },
              },
            }}
          >
            {availableYears.map((year) => (
              <StyledMenuItem key={year.year} value={year.year}>
                {year.year}
              </StyledMenuItem>
            ))}
          </Select>
        </StyledFormControl>
      </DialogHeader>
      <Divider />
      <DialogContent
        sx={{ padding: "1rem", backgroundColor: "#121212", height: "100%" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#464646" />
            <XAxis
              dataKey="date"
              tick={{ fill: "#ffffff", fontSize: 12 }}
              tickFormatter={formatXAxisTick}
              interval={0}
            />
            <YAxis
              domain={yAxisDomain}
              tick={{ fill: "#ffffff" }}
              label={{
                value: "수량",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "#ffffff" },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              {...customLegendProps}
              wrapperStyle={{
                color: "#ffffff",
                paddingBottom: "20px",
              }}
            />
            <Line
              type="monotone"
              dataKey="입고누적"
              stroke="#22c55e"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, stroke: "#22c55e", strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="출고누적"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, stroke: "#3b82f6", strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="재고량"
              stroke="#ff8787"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, stroke: "#ff8787", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </DialogContent>
    </StyledDialog>
  );
}

export default DayStockGraph;
